"use client";

import { useEffect, useState } from "react";

type Platform = "android" | "ios" | null;

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export default function InstallBanner() {
  const [platform, setPlatform] = useState<Platform>(null);
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Bereits installiert (standalone) oder schon weggeklickt → nicht zeigen
    if (window.matchMedia("(display-mode: standalone)").matches) return;
    if (sessionStorage.getItem("install-dismissed")) return;

    const ua = navigator.userAgent;
    const isIos =
      /iphone|ipad|ipod/i.test(ua) && !(window as unknown as { MSStream?: unknown }).MSStream;
    const isAndroid = /android/i.test(ua);

    if (isIos) setPlatform("ios");

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      if (isAndroid) setPlatform("android");
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  function dismiss() {
    sessionStorage.setItem("install-dismissed", "1");
    setDismissed(true);
  }

  async function install() {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setDismissed(true);
    else dismiss();
  }

  if (dismissed || !platform) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 max-w-sm mx-auto">
      <div className="bg-white rounded-2xl shadow-2xl shadow-purple-200 border border-purple-100 p-4 flex items-start gap-3">
        {/* Icon */}
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-purple-400 flex items-center justify-center flex-shrink-0">
          <svg width="24" height="24" viewBox="0 0 100 100" fill="none">
            <path
              d="M50 12c-22 0-40 14-40 32 0 9 5 17 13 23l-4 16 18-10c4 1 9 2 13 2 22 0 40-14 40-32S72 12 50 12z"
              fill="white"
            />
            <circle cx="36" cy="44" r="5" fill="#7c3aed" />
            <circle cx="50" cy="44" r="5" fill="#7c3aed" />
            <circle cx="64" cy="44" r="5" fill="#7c3aed" />
          </svg>
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-purple-900 text-sm">
            KidChat installieren
          </p>
          {platform === "android" ? (
            <p className="text-xs text-purple-500 mt-0.5">
              App auf dem Startbildschirm speichern
            </p>
          ) : (
            <p className="text-xs text-purple-500 mt-0.5">
              Tippe auf{" "}
              <span className="inline-flex items-center gap-0.5 font-medium text-purple-700">
                {/* iOS Share-Icon */}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                  <polyline points="16 6 12 2 8 6" />
                  <line x1="12" y1="2" x2="12" y2="15" />
                </svg>
                Teilen
              </span>{" "}
              → „Zum Home-Bildschirm"
            </p>
          )}
        </div>

        {/* Buttons */}
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          {platform === "android" && (
            <button
              onClick={install}
              className="text-xs font-semibold text-white bg-violet-600 hover:bg-violet-700 rounded-lg px-3 py-1.5 transition-colors"
            >
              Installieren
            </button>
          )}
          <button
            onClick={dismiss}
            className="text-xs text-purple-400 hover:text-purple-600 transition-colors"
          >
            Nicht jetzt
          </button>
        </div>
      </div>
    </div>
  );
}
